import React, { useEffect, useState } from 'react';
import '../styles/ResultContainer.css';
import { useTranslation } from 'react-i18next';

const ResultContainer = ({ result }) => {
  const { t } = useTranslation();
  const [animate, setAnimate] = useState(false);
  const [, forceUpdate] = useState();
  
  useEffect(() => {
    // Trigger animation when result changes
    setAnimate(false);
    const timer = setTimeout(() => setAnimate(true), 100);
    
    // Force re-render when i18n resources are reloaded
    const handleI18nReload = () => {
      forceUpdate({});
    };
    
    window.addEventListener('i18nextReloaded', handleI18nReload);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('i18nextReloaded', handleI18nReload);
    };
  }, [result]);

  // Force component to re-render when language changes
  useEffect(() => {
    const handleLanguageChanged = () => {
      forceUpdate({});
    };
    
    window.addEventListener('languageChanged', handleLanguageChanged);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChanged);
    };
  }, []);

  if (!result) return null;

  if (result.error) {
    return (
      <div className="futuristic-error">
        <div className="error-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <p>{result.error}</p>
      </div>
    );
  }

  if (result.decision === "ERROR") {
    return (
      <div className="futuristic-error">
        <div className="error-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <p>{result.error || t('results.errors.invalidTicker')}</p>
      </div>
    );
  }

  // Find the highest confidence action
  const highestAction = Object.entries({
    buy: result.buy,
    sell: result.sell,
    hold: result.hold
  }).sort((a, b) => b[1] - a[1])[0][0];

  return (
    <div className={`futuristic-result enhanced-result ${animate ? 'animate' : ''}`}>
      <div className="result-glow"></div>
      <div className="ticker-header">
        <div className="ticker-info">
          <h2>{result.ticker}</h2>
          <div className="ticker-badge">
            <span className="badge-icon">⚡</span>
            <span className="badge-text">Live Analysis</span>
          </div>
        </div>
        <div className="price-container">
          <span className="price" title={result.currencyName}>
            <span className="currency-symbol">{result.currencySymbol}</span>
            {result.price.toFixed(2)}
          </span>
          <div className="time-display">
            <span className="time-icon">🕐</span>
            <span className="time-text">{new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} · {new Date().toLocaleDateString([], {month: 'short', day: 'numeric', year: 'numeric'})}</span>
          </div>
        </div>
      </div>
      
      <div className="ai-analysis-dashboard">
        <div className="analysis-visualization">
          {/* AI Decision Badge */}
          <div className="ai-decision-badge">
            <div className={`decision-card ${highestAction}`}>
              <div className="decision-glow"></div>
              <div className="decision-content">
                <div className="decision-label">AI Recommendation</div>
                <div className={`decision-value ${highestAction}`}>{result.decision}</div>
                <div className="decision-confidence">
                  {getConfidenceLevel(getHighestPercent(result))} Confidence
                </div>
              </div>
            </div>
          </div>

          {/* Bar Chart Visualization */}
          <div className="ai-bar-chart">
            <div className="bar-chart-container">
              <div className="bar-item buy-bar">
                <div className="bar-header">
                  <div className="bar-label">
                    <div className="bar-indicator buy-indicator"></div>
                    <span>{t('results.actions.buy')}</span>
                  </div>
                  <div className="bar-percentage">{result.buy}%</div>
                </div>
                <div className="bar-track">
                  <div 
                    className="bar-fill buy-fill" 
                    style={{ width: `${result.buy}%` }}
                  >
                    <div className="bar-shimmer"></div>
                  </div>
                </div>
              </div>

              <div className="bar-item sell-bar">
                <div className="bar-header">
                  <div className="bar-label">
                    <div className="bar-indicator sell-indicator"></div>
                    <span>{t('results.actions.sell')}</span>
                  </div>
                  <div className="bar-percentage">{result.sell}%</div>
                </div>
                <div className="bar-track">
                  <div 
                    className="bar-fill sell-fill" 
                    style={{ width: `${result.sell}%` }}
                  >
                    <div className="bar-shimmer"></div>
                  </div>
                </div>
              </div>

              <div className="bar-item hold-bar">
                <div className="bar-header">
                  <div className="bar-label">
                    <div className="bar-indicator hold-indicator"></div>
                    <span>{t('results.actions.hold')}</span>
                  </div>
                  <div className="bar-percentage">{result.hold}%</div>
                </div>
                <div className="bar-track">
                  <div 
                    className="bar-fill hold-fill" 
                    style={{ width: `${result.hold}%` }}
                  >
                    <div className="bar-shimmer"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="disclaimer enhanced-disclaimer">
        <div className="disclaimer-icon">⚠️</div>
        <div className="disclaimer-content">
          {t('results.disclaimer')}
        </div>
      </div>
    </div>
  );
};

// Helper function to determine confidence level
function getConfidenceLevel(percentage) {
  if (percentage >= 70) return "High";
  if (percentage >= 50) return "Medium";
  return "Low";
}

// Helper function to find the highest percentage
function getHighestPercent(result) {
  return Math.max(result.buy, result.sell, result.hold);
}

export default ResultContainer;